import type { ExperienceItem } from "@/lib/types";
import { isUserFacingValidUrl } from "@/lib/experiences/linkIntegrity";
import { translate } from "@/lib/i18n/useI18n";
import type { Language } from "@/lib/i18n/languages";

// ─── Provider link state (Marketplace Product Detail & Conversion V1) ──────
// Single source of truth for whether a provider item's booking_url is
// usable — shared by ExperienceMarketplaceRow, FeaturedShelf, and
// ExperienceDetailDrawer so the three surfaces can never disagree about
// whether a "Ver disponibilidad" CTA or an "Enlace no disponible" state is
// the honest one to show for the same item. Previously this exact
// three-line check was duplicated in each component; extracted here so it's
// a plain, directly unit-testable function instead of a guardrail-grepped
// inline expression.

export type ProviderLinkState = "bookable" | "missing_link" | "not_applicable";

/**
 * "bookable"       — a provider item with a real, usable booking_url.
 * "missing_link"   — a provider item whose booking_url is absent/invalid.
 * "not_applicable" — a local/reference item; it never has a bookable link.
 *
 * Real Data Policy: an explicit backend `link_available: false` always wins,
 * but `true` is not sufficient by itself: the concrete booking_url must also
 * pass the frontend integrity check before we render an external anchor.
 * Never guesses or constructs a URL.
 */
export function getProviderLinkState(exp: ExperienceItem): ProviderLinkState {
  if (exp.source_type !== "provider") return "not_applicable";
  const hasBooking = exp.link_available !== false
    && typeof exp.booking_url === "string"
    && isUserFacingValidUrl(exp.booking_url);
  return hasBooking ? "bookable" : "missing_link";
}

/**
 * Provider-Only Marketplace & Comparison V2 Foundation — the frontend
 * mirror of the backend's engine/supply_integration/marketplace_search_
 * filters.py::is_provider_only_eligible(): source_type === "provider",
 * source_class === "LIVE_PROVIDER", product_eligibility_status ===
 * "eligible", and a real bookable link. /experiences/search already
 * enforces this server-side; this exists for the temporary /experiences/
 * sequence fallback path (task 6), whose marketplace_inventory does not go
 * through the same final-response eligibility filter as days[] — so a
 * provider item that never should have been marketplace-eligible could
 * otherwise leak into the launch marketplace only while the fallback is
 * active. Never used to gate booking (getProviderLinkState already does
 * that) — only to decide launch-marketplace VISIBILITY.
 */
export function isMarketplaceEligibleProvider(exp: ExperienceItem): boolean {
  return (
    exp.source_type === "provider"
    && exp.source_class === "LIVE_PROVIDER"
    && exp.product_eligibility_status === "eligible"
    && getProviderLinkState(exp) === "bookable"
  );
}

/**
 * Marketplace Launch Conversion Audit V1 — production audit found the
 * backend's real provider_name value is a lowercase slug ("viator"), which
 * every card/drawer/comparison surface was rendering verbatim ("· viator",
 * "se completan con viator."). Pure display capitalization of a field the
 * backend already returned — never invents or substitutes a provider name,
 * just title-cases the one that's there (works for any future provider,
 * not just Viator).
 *
 * Marketplace Visual Polish V1, Parte D — naive per-word title-casing turns
 * "guruwalk" into "Guruwalk" (only the leading letter capitalizes), never
 * the brand's actual "GuruWalk" casing. A known-provider lookup fixes the
 * providers we already integrate with exact casing; any other/future
 * provider key still falls back to the same generic title-case behavior.
 */
const KNOWN_PROVIDER_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  viator:   "Viator",
  guruwalk: "GuruWalk",
};

export function formatProviderDisplayName(name: string | null | undefined): string | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  const known = KNOWN_PROVIDER_DISPLAY_NAMES[trimmed.toLowerCase()];
  if (known) return known;
  return trimmed.replace(/\S+/g, word => word.charAt(0).toUpperCase() + word.slice(1));
}

/**
 * Full-Width Marketplace Grid & Scalable Provider Filter V1 — the ONE
 * normalization rule for comparing a provider filter selection against
 * exp.provider_name: trim + lowercase, mirroring the backend's own
 * normalize_provider_key() (engine/supply_integration/
 * marketplace_search_filters.py) exactly. Never a display-label
 * comparison — formatProviderDisplayName() above is purely presentational
 * and must never feed back into a filter/facet key.
 */
export function normalizeProviderKey(name: string | null | undefined): string {
  return (name ?? "").trim().toLowerCase();
}

/**
 * Provider Landing Accuracy + Query Expansion Prep V1 — production audit
 * confirmed booking_url is a real, product-specific Viator URL (same path
 * search-list and /providers/viator/products/{code} both return), but
 * Viator's own product-page template renders it with a destination-wide
 * recommendations rail around the selected product — normal behavior for
 * Viator's product pages, not something a different URL shape avoids (see
 * scripts/provider_landing_accuracy_audit.py). "Ver experiencia" therefore
 * overpromised a 100%-dedicated page; the honest framing names where the
 * click actually goes and that the reservation happens there.
 */
// Sprint: Frontend UI i18n Foundation V1, Parte C — `language` is an
// OPTIONAL 2nd parameter defaulting to "es", so every EXISTING single-arg
// call site/test (providerLinkState.test.ts) keeps returning byte-for-byte
// the same Spanish string; only a caller that explicitly passes a language
// gets translated copy. `displayName` itself (the provider's own name) is
// NEVER translated — only the surrounding sentence template is.
export function getProviderSupportingText(exp: ExperienceItem, language: Language = "es"): string {
  const displayName = exp.source_type === "provider"
    ? formatProviderDisplayName(exp.provider_name)
    : null;
  return displayName
    ? translate(language, "marketplace.provider.supportingText", { provider: displayName })
    : translate(language, "marketplace.provider.supportingTextFallback");
}

/**
 * Provider Landing Accuracy + Query Expansion Prep V1 — CTA label. Never
 * "Ver experiencia" (implies a 100%-dedicated page) — Viator's product page
 * shows the selected product as the focus but surrounded by its own
 * recommendations rail, so the honest verb is "continue", not "view".
 * Names the real provider when known, same fallback pattern as
 * getProviderSupportingText — never hardcodes "Viator" for a future
 * provider, but produces exactly "Continuar en Viator" for the only live
 * provider today.
 */
export function getProviderContinueCta(exp: ExperienceItem, language: Language = "es"): string {
  const displayName = exp.source_type === "provider"
    ? formatProviderDisplayName(exp.provider_name)
    : null;
  return displayName
    ? translate(language, "marketplace.provider.continueCta", { provider: displayName })
    : translate(language, "marketplace.provider.continueFallback");
}

/**
 * Launch Provider Detail Quality V1 — the single mapping for a Viator
 * cancellation_policy_type code, shared by every surface that renders rich
 * product detail (ExperienceDetailDrawer, ComparisonDrawer,
 * ProviderProductDetailDrawer). Previously three separate inline copies
 * that could quietly drift apart; unknown codes pass through verbatim
 * rather than being hidden, so a new backend value is never silently lost.
 */
export function formatCancellationPolicy(type: string | null | undefined, language: Language = "es"): string | null {
  if (!type) return null;
  if (type === "STANDARD") return translate(language, "detail.cancellationStandard");
  if (type === "ALL_SALES_FINAL") return translate(language, "detail.cancellationNoRefund");
  return type;
}

/**
 * Sprint: Frontend i18n Completion V1.1 — shared home for the "price and/or
 * availability still needs confirming" trust note, previously duplicated
 * verbatim (hardcoded Spanish) in FeaturedShelf.tsx, ExperienceMarketplaceRow.tsx,
 * and ExperienceDetailDrawer.tsx. Same "own-affordance flags computed by the
 * caller, this function only picks the copy" contract as the helpers above —
 * never re-derives isPriceOrientative/isAvailabilityUncertain itself.
 */
export function getPriceAvailabilityTrustNote(
  isPriceOrientative: boolean,
  isAvailabilityUncertain: boolean,
  language: Language = "es",
): string | null {
  if (isPriceOrientative && isAvailabilityUncertain) {
    return translate(language, "marketplace.priceTrust.priceAndAvailability");
  }
  if (isPriceOrientative) return translate(language, "marketplace.priceTrust.priceOrientative");
  if (isAvailabilityUncertain) return translate(language, "marketplace.priceTrust.availabilityUncertain");
  return null;
}
